import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Bell, Settings, LogOut, Moon, Sun, Check, X, Eye } from 'lucide-react';
import { CognitoAuthentication } from '../../cognito/cognito-helper';
import { getText } from '@/translate/text';
import { AppLocalEnum, getLanguage } from '@/translate/text';
import { useHasPermission } from '@/Utils/helpers';

const getNotificationColorClass = (type) => {
  switch (type) {
    case 'success':
      return 'bg-green-50 dark:bg-green-900';
    case 'error':
      return 'bg-red-50 dark:bg-red-900';
    case 'warning':
      return 'bg-yellow-50 dark:bg-yellow-900';
    default:
      return 'bg-primary-50 dark:bg-primary-900';
  }
};

const formatTimeDiff = (timeDiff) => {
  if (timeDiff < 0) {
    return `${Math.abs(timeDiff)} mins before`;
  }
  return `${timeDiff} mins past`;
};

const Header = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { toggleTheme, currentTheme } = useUIStore();
  const language = localStorage.getItem('language') || AppLocalEnum.EN;
  const isRTL = language === AppLocalEnum.AR;

  const [lang, setLang] = useState(localStorage.getItem('language') || 'en');
  useEffect(() => {
    setLang(localStorage.getItem('language') || AppLocalEnum.EN);
  }, [localStorage.getItem('language')]);
  const { hasPermission: hasAdminAccess } = useHasPermission('access_admin_panel');
  const handleLogout = async () => {
    try {
      const user = await CognitoAuthentication().logout();
      if (user) {
        navigate('/');
      } else {
        alert('Please login first');
      }
    } catch (error) {
      alert(error);
    }
  };

  const userNavigation = [
    {
      key: 'profile_settings',
      name: 'Profile Settings',
      href: '/settings/profile',
      icon: Settings,
    },
    ...(hasAdminAccess
      ? [
          {
            key: 'admin_dashboard',
            name: 'Admin Dashboard',
            href: '/admin',
            icon: Settings,
          },
        ]
      : []),
    {
      key: 'sign_out',
      name: 'Sign out',
      onClick: handleLogout,
      icon: LogOut,
    },
  ];

  return (
    <header
      className="fixed top-0 z-30 w-full bg-white dark:bg-gray-800 shadow-sm"
      style={{ direction: lang === 'ar' ? 'rtl' : 'ltr' }}
    >
      <div className="h-16 px-4 flex items-center justify-between">
        <div className="flex items-center">{/* Logo placeholder */}</div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <Menu as="div" className="relative">
            <Menu.Button className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 relative"></Menu.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Menu.Items
                className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-[28rem] bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {getText('notifications')}
                    </h3>
                  </div>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>

          {/* User Menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full">
              <span className="sr-only">{getText('open_user_menu')}</span>
              <img
                className="h-8 w-8 rounded-full"
                src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.name || 'User'}`}
                alt="User avatar"
              />
            </Menu.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Menu.Items
                className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden`}
              >
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) =>
                      item.href ? (
                        <a
                          href={item.href}
                          className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''} group flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                        >
                          <item.icon
                            className={`h-5 w-5 text-gray-400 dark:text-gray-500 ${isRTL ? 'ml-3' : 'mr-3'}`}
                          />
                          {getText(item.key) || item.name}
                        </a>
                      ) : (
                        <button
                          onClick={item.onClick}
                          className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''} w-full group flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                        >
                          <item.icon
                            className={`h-5 w-5 text-gray-400 dark:text-gray-500 ${isRTL ? 'ml-3' : 'mr-3'}`}
                          />
                          {getText(item.key) || item.name}
                        </button>
                      )
                    }
                  </Menu.Item>
                ))}

                {/* Change Language Button */}
                <Menu.Item key="change_language">
                  {({ active }) => (
                    <button
                      onClick={() => {
                        const newLang = lang === 'en' ? 'ar' : 'en';
                        localStorage.setItem('language', newLang);
                        setLang(newLang);
                        window.location.reload();
                      }}
                      className={`${active ? 'bg-gray-100 dark:bg-gray-700' : ''} w-full group flex items-center justify-between px-8 py-2 text-sm text-gray-700 dark:text-gray-300 gap-4`}
                      style={{ direction: 'ltr' }}
                    >
                      <span>AR</span>
                      <span
                        className={`relative inline-block w-10 h-5 transition duration-200 ease-in-out ${lang === 'en' ? 'bg-green-400' : 'bg-blue-400'} rounded-full`}
                      >
                        <span
                          className={`absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition ${lang === 'en' ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </span>
                      <span>EN</span>
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Header;
